import { useState, useRef, useEffect } from 'react';
import { api } from '../services/api';
import { Poster } from '../types';
import { filesize } from 'filesize';
import Button from './ui/Button';

interface PosterUploadProps {
  onUploadComplete: (poster: Poster) => void;
  onError: (error: string) => void;
}

const thinkingMessages = [
  "Uploading your poster to secure storage...",
  "Reading your poster like a curious researcher...",
  "Analyzing poster structure and layout...",
  "Extracting text and identifying sections...",
  "Detecting figures and charts...",
  "Reading author information and affiliations...",
  "Identifying key takeaways...",
  "Mapping figure positions...",
  "Almost there! Organizing your content...",
];

export default function PosterUpload({ onUploadComplete, onError }: PosterUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [currentMessage, setCurrentMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Rotate messages during upload
  useEffect(() => {
    if (!isUploading) {
      setCurrentMessage('');
      return;
    }

    let messageIndex = 0;
    setCurrentMessage(thinkingMessages[0]);

    const interval = setInterval(() => {
      messageIndex = (messageIndex + 1) % thinkingMessages.length;
      setCurrentMessage(thinkingMessages[messageIndex]);
    }, 2500);

    return () => clearInterval(interval);
  }, [isUploading]);

  const validateAndSelectFile = (file: File) => {
    setFileError(null);

    // Validate file type
    if (!file.type.match(/^(image\/(png|jpeg|jpg)|application\/pdf)$/)) {
      setFileError('Invalid file type. Please upload a PNG, JPG, or PDF file.');
      setSelectedFile(null);
      return false;
    }

    // File is valid
    setSelectedFile(file);
    return true;
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const poster = await api.uploadPoster(selectedFile);
      onUploadComplete(poster);
      // Reset state after successful upload
      setSelectedFile(null);
      setFileError(null);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to upload poster');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setFileError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSelectFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSelectFile(file);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('image')) return '🖼️';
    return '📎';
  };

  return (
    <div
      className={[
        'border-2 border-dashed rounded-xl p-12 sm:p-16 text-center',
        'transition-all duration-300',
        isDragging && 'border-primary-400 bg-primary-50',
        !isDragging && fileError && 'border-red-400 bg-red-50',
        !isDragging && !fileError && selectedFile && 'border-green-400 bg-green-50',
        !isDragging && !fileError && !selectedFile && 'border-neutral-300 bg-white hover:border-primary-300 hover:bg-neutral-50',
        (selectedFile || isUploading) ? 'cursor-default' : 'cursor-pointer',
      ].filter(Boolean).join(' ')}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => !selectedFile && !isUploading && fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,application/pdf"
        onChange={handleFileSelect}
        className="hidden"
      />

      {isUploading ? (
        <div>
          <div className="text-5xl sm:text-6xl mb-4">⏳</div>
          <p className="text-lg sm:text-xl font-semibold mb-2 text-neutral-900">
            Processing poster...
          </p>
          <p className="text-sm sm:text-base text-neutral-600 min-h-[20px]">
            {currentMessage}
          </p>
        </div>
      ) : selectedFile ? (
        <div>
          {fileError ? (
            <>
              <div className="text-5xl sm:text-6xl mb-4">❌</div>
              <p className="text-lg sm:text-xl font-semibold mb-2 text-red-800">
                {selectedFile.name}
              </p>
              <p className="text-sm sm:text-base text-red-600 mb-6">
                {fileError}
              </p>
              <Button onClick={() => fileInputRef.current?.click()}>
                Select Different File
              </Button>
            </>
          ) : (
            <>
              <div className="text-5xl sm:text-6xl mb-4">✅</div>
              <p className="text-lg sm:text-xl font-semibold mb-2 text-neutral-900">
                {selectedFile.name}
              </p>
              <p className="text-sm sm:text-base text-neutral-600 mb-6">
                {getFileIcon(selectedFile.type)} {filesize(selectedFile.size)}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="success" onClick={handleUpload}>
                  Upload Poster
                </Button>
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div>
          <div className="text-5xl sm:text-6xl mb-4">📄</div>
          <p className="text-lg sm:text-xl font-semibold mb-2 text-neutral-900">
            Drop your poster here or click to upload
          </p>
          <p className="text-sm sm:text-base text-neutral-600">
            Supports PNG, JPG, and PDF files (max 50MB)
          </p>
        </div>
      )}
    </div>
  );
}
